import { Drawer, DrawerRootProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { noop } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useMeasure } from 'react-use';
import { darkModeRoot } from 'src/app/App.css';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui/Stack';
import { useMatchMedia } from 'src/hooks/useMatchMedia';

import { useSiteTheme } from '../SiteTheme/SiteThemeContext';
import * as styles from './MobileDrawerContext.css';

type EditableRootProps = {
  title?: React.ReactNode;
  /** When true, shows the drag handle bar instead of the back button header. Defaults to false. */
  showHandleBar?: boolean;
} & Pick<DrawerRootProps, 'size' | 'position' | 'zIndex'>;

export type IMobileDrawerContext = {
  show: boolean;
  openWithContent: (
    content: React.ReactNode,
    props?: EditableRootProps
  ) => void;
  close: () => void;
  toggle: () => void;
};

const MobileDrawerContext = createContext<IMobileDrawerContext>({
  show: false,
  openWithContent: noop,
  close: noop,
  toggle: noop,
});

export const useMobileDrawerContext = () => useContext(MobileDrawerContext);

export const MobileDrawerProvider = ({ children }: PropsWithChildren) => {
  const { isDarkMode } = useSiteTheme();
  const isMobileLayout = useMatchMedia('mobile');
  const [headerRef, { height }] = useMeasure<HTMLElement>();

  const [show, { open, close, toggle }] = useDisclosure();
  const [content, setContent] = useState<React.ReactNode>();
  const [rootProps, setRootProps] = useState<EditableRootProps>();

  const openWithContent = useCallback(
    (content: React.ReactNode, props?: EditableRootProps) => {
      setContent(content);
      setRootProps(props);
      open();
    },
    [open]
  );

  const sanitizedPosition = useMemo(
    () => rootProps?.position ?? 'bottom',
    [rootProps?.position]
  );

  const isFullScreen = useMemo(
    () => rootProps?.size === '100%',
    [rootProps?.size]
  );

  if (!isMobileLayout) {
    return <>{children}</>;
  }

  return (
    <MobileDrawerContext.Provider
      value={{ show, openWithContent, close, toggle }}
    >
      {children}
      <Drawer.Root
        opened={show}
        onClose={close}
        size={rootProps?.size ?? '50%'}
        zIndex={rootProps?.zIndex ?? 200}
        position={sanitizedPosition}
        className={clsx(globalMobileWrapper, {
          [darkModeRoot]: isDarkMode,
        })}
        closeOnClickOutside
      >
        <Drawer.Overlay />
        <Drawer.Content
          bg={vars.color.backgroundSecondary}
          className={clsx({
            [styles.rootBorderRadius]:
              !isFullScreen && sanitizedPosition === 'bottom',
          })}
          radius={!isFullScreen && sanitizedPosition === 'bottom' ? 'md' : 0}
          style={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          {(!isFullScreen || rootProps?.title) && (
            <Drawer.Header
              bg={vars.color.backgroundSecondary}
              ref={headerRef}
              onClick={toggle}
              className={styles.header}
            >
              {rootProps?.title && !rootProps?.showHandleBar ? (
                <MobileHeaderBar onBack={close} title={rootProps.title} />
              ) : (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  width="full"
                  direction="column"
                >
                  <div className={styles.headerBar}></div>
                  {rootProps?.title && (
                    <div className={styles.handleBarTitle}>
                      {rootProps.title}
                    </div>
                  )}
                </Stack>
              )}
            </Drawer.Header>
          )}
          <Drawer.Body
            style={{
              height: `calc(100% - ${height + 20}px)`,
              overflowY: 'auto',
            }}
            bg={vars.color.backgroundSecondary}
          >
            {content}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
    </MobileDrawerContext.Provider>
  );
};
